/*!
 * plid-advisor-core.js — der deterministische Beratungs-Kern (OHNE DOM).
 *
 * HARTER WERT: Dieses Modul erfindet KEINE Geschäftsregeln. Es liest die aus der
 * Python-`berate()` exportierte Datei (assets/advisor-data.json) und macht reines
 * Nachschlagen + mechanisches Rechnen (Breakpoint-Lookup, Preis-Overlay, Rabatt).
 * Der Golden-Test (scripts/advisor_golden_check.mjs) beweist: JS-Ergebnis == Python.
 *
 * Läuft in zwei Welten aus EINER Quelle:
 *   - Browser:  window.PlidAdvisorCore = { ... }   (kein Modul-System nötig)
 *   - Node/ESM: `import { createEngine } from './plid-advisor-core.js'` (Golden-Test)
 *
 * Die UI (plid-advisor.js) importiert diese Funktionen und macht NUR Darstellung.
 */
(function (root, factory) {
  var api = factory();
  // Browser-Global
  if (typeof window !== "undefined") { window.PlidAdvisorCore = api; }
  // CommonJS (falls je gebraucht)
  if (typeof module !== "undefined" && module.exports) { module.exports = api; }
  root.__PlidAdvisorCore = api; // für ESM-Bridge unten
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  function round2(n) { return Math.round(n * 100) / 100; }
  function round4(n) { return Math.round(n * 10000) / 10000; }

  // format_de: "%g" auf 3 Stellen + deutsches Komma (identisch zu optimiser.format_de).
  function formatDe(value) {
    var r = Math.round(value * 1000) / 1000;
    // JS "%g"-Äquivalent: kürzeste Zahl ohne überflüssige Nullen.
    return String(r).replace(".", ",");
  }

  // Portierung von optimise_single_size (engine/optimiser.py): EINE Gebindegröße,
  // aufgerundet. Wahl: wenigste Gebinde, bei Gleichstand geringster Verschnitt.
  // packs = [{size, price, variant_id, image}, …]. Gibt {index, n, size} oder null.
  function optimiseSingleSize(needed, packs) {
    if (needed <= 0 || !packs.length) return null;
    var best = null; // [ [n, totalRounded], index, n, total ]
    for (var i = 0; i < packs.length; i++) {
      var size = packs[i].size;
      if (size <= 0) continue;
      var n = Math.ceil(needed / size - 1e-9);
      var total = n * size;
      var key = [n, round4(total)];
      if (best === null || key[0] < best[0][0] ||
          (key[0] === best[0][0] && key[1] < best[0][1])) {
        best = [key, i, n, total];
      }
    }
    if (best === null) return null;
    return { index: best[1], n: best[2], size: packs[best[1]].size };
  }

  // Größe (Liter) aus einem quantity_label wie "2× 5-L-Eimer" / "1× 2,5-L-Eimer".
  // Nur für die Farb-Varianten-Zuordnung (Farbe × Größe → variant_id).
  function sizeFromLabel(label) {
    if (!label) return null;
    var m = /([0-9]+(?:[.,][0-9]+)?)\s*-?\s*L/i.exec(label);
    if (!m) return null;
    return parseFloat(m[1].replace(",", "."));
  }
  function sizeKey(size) {
    if (size == null) return null;
    // identisch zu Python _size_str: "%g" mit Rundung auf 4 Stellen
    var r = Math.round(size * 10000) / 10000;
    return String(r);
  }

  // Erste gatende Antwort in Frage-Reihenfolge (spiegelt berate()/Export exakt).
  function firstGate(fall, answers) {
    var fragen = fall.schema.fragen || [];
    for (var i = 0; i < fragen.length; i++) {
      var q = fragen[i];
      if (q.typ !== "select") continue;
      var val = answers[q.id];
      if (val == null) continue;
      var opt = (q.optionen || []).filter(function (o) { return o.value === val; })[0];
      if (opt && opt.terminal) return { id: q.id, value: val };
    }
    return null;
  }

  // Effektive Select-Antworten bis zum ersten Gate (inkl.) — wie im Export.
  function effectiveSelect(fall, answers) {
    var eff = {};
    var fragen = fall.schema.fragen || [];
    for (var i = 0; i < fragen.length; i++) {
      var q = fragen[i];
      if (q.typ !== "select") continue;
      var val = answers[q.id];
      if (val == null) continue;
      eff[q.id] = val;
      var opt = (q.optionen || []).filter(function (o) { return o.value === val; })[0];
      if (opt && opt.terminal) break;
    }
    return eff;
  }

  function variantKey(sel) {
    return Object.keys(sel).sort().map(function (k) { return k + "=" + sel[k]; }).join("&");
  }

  // Vlies-Band (Fläche → Größen-Label), spiegelt build_case: ≤25→10m, ≤60→25m, sonst 50m.
  function bandFor(bands, area) {
    for (var i = 0; i < bands.length; i++) {
      var thr = bands[i][0];
      if (thr === null || area <= thr) return bands[i][1];
    }
    return bands[bands.length - 1][1];
  }

  // Eine Rezept-Position mechanisch zu einem Line-Item rechnen (packt/zählt).
  // catalog = geteilte Produkt-/Gebinde-Daten (data.catalog). Reproduziert
  // pack_item/count_item aus dem Backend 1:1 (Golden-Test bürgt dafür).
  function computeLine(item, area, coats, catalog) {
    var typ = item.type;
    var base = { position: item.position, category: item.category, optional: !!item.optional,
                 catalog_key: item.k, note: item.note || "" };

    if (typ === "pack") {
      var cat = catalog[item.k] || { unit_label: "L-Eimer", gebinde: [] };
      var packs = cat.gebinde || [];
      var actualCoats = (item.coats === "user") ? coats : item.coats;
      var liters = area / item.coverage * actualCoats;
      var sol = optimiseSingleSize(liters, packs);
      var n = sol ? sol.n : 1;
      var chosen = sol ? packs[sol.index] : { size: 0, price: 0, variant_id: null, image: "" };
      var qlabel = sol ? (n + "× " + formatDe(chosen.size) + "-" + cat.unit_label) : "—";
      // note wie im Backend: Basis + " · Bedarf X L (N Anstrich(e) · cov m²/L)"
      var bedarf = "Bedarf " + formatDe(round1(liters)) + " L (" + actualCoats +
                   " Anstrich" + (actualCoats !== 1 ? "e" : "") + " · " + formatDe(item.coverage) + " m²/L)";
      var note = item.note_base ? (item.note_base + " · " + bedarf) : bedarf;
      return finalize(base, {
        quantity_label: qlabel, pack_count: n, unit_price: chosen.price,
        image: chosen.image, variant_id: chosen.variant_id, note: note, cart_qty: n,
        handle: cat.handle,
      });
    }

    if (typ === "count_ceil") {
      var c1 = catalog[item.k] || { unit_label: "Stück", price: 0, variant_id: null, image: "" };
      var cnt = Math.max(1, Math.ceil(area / item.per_m2));
      return finalize(base, {
        quantity_label: cnt + "× " + c1.unit_label, pack_count: cnt, unit_price: c1.price,
        image: c1.image, variant_id: c1.variant_id, note: item.note || "", cart_qty: cnt,
        handle: c1.handle,
      });
    }

    if (typ === "count_band") {
      var cb = catalog[item.k] || { bands: {} };
      var band = bandFor(item.bands, area);
      var v = (cb.bands || {})[band] || { unit_label: "Rolle", price: 0, variant_id: null, image: "" };
      // Positionsname trägt die Größe: "Abdeckvlies 25m" (wie build_case).
      var pos = "Abdeckvlies " + band;
      return finalize({ position: pos, category: item.category, optional: !!item.optional, catalog_key: item.k, note: item.note || "" }, {
        quantity_label: "1× " + v.unit_label, pack_count: 1, unit_price: v.price,
        image: v.image, variant_id: v.variant_id, note: item.note || "", cart_qty: 1,
        handle: cb.handle,
      });
    }

    // count_fix (Goldband / Maler-Set / feste Stück-Variante), genau 1.
    var cf = catalog[item.k] || { unit_label: "Stück", price: 0, variant_id: null, image: "" };
    return finalize(base, {
      quantity_label: "1× " + cf.unit_label, pack_count: 1, unit_price: cf.price,
      image: cf.image, variant_id: cf.variant_id, note: item.note || "", cart_qty: 1,
      handle: cf.handle,
    });
  }

  function round1(n) { return Math.round(n * 10) / 10; }

  function finalize(base, v) {
    return {
      position: base.position, category: base.category, quantity_label: v.quantity_label,
      pack_count: v.pack_count, unit_price: v.unit_price, note: v.note,
      optional: base.optional, catalog_key: base.catalog_key, image: v.image || "",
      handle: v.handle || "",
      variant_id: v.variant_id,
      cart: (v.variant_id && v.cart_qty > 0) ? [{ id: v.variant_id, quantity: v.cart_qty }] : [],
    };
  }

  /**
   * Kern-Auswertung: (fall-Daten, Antworten) → Ergebnis-Objekt, formgleich zur
   * Python-berate()-Ausgabe. Rezept (je Antwort-Pfad) + Katalog (geteilt) →
   * mechanisches Packen/Zählen. KEINE Geschäftsregeln hier — nur Rechnen.
   *
   * answers: { <select-ids>, area_m2, coats, mit_werkzeug, color? }
   */
  function evaluate(fall, answers, catalog) {
    // 1) Gate?
    var gate = firstGate(fall, answers);
    if (gate) {
      var gkey = gate.id + "=" + gate.value;
      var g = fall.gates[gkey];
      if (!g) throw new Error("Gate ohne Export-Eintrag: " + gkey + " (Fall " + fall.case_id + ")");
      var r = g.result;
      return {
        status: r.status, ehrlicher_pfad: r.ehrlicher_pfad, produkt: r.produkt,
        ueberschrift: r.ueberschrift, nachricht: r.nachricht,
        passt_fuer: r.passt_fuer || [], passt_nicht_fuer: r.passt_nicht_fuer || [],
        warnungen: r.warnungen || [], events: r.events || [],
        weiter_case_id: r.weiter_case_id || null,
        line_items: null, total: null,
      };
    }

    // 2) Empfehlungs-Pfad → Rezept nachschlagen + rechnen
    var eff = effectiveSelect(fall, answers);
    var vkey = variantKey(eff);
    var variant = fall.variants[vkey];
    if (!variant) throw new Error("Kein Variant-Eintrag: " + vkey + " (Fall " + fall.case_id + ")");
    var pres = variant.presentation;

    var coats = parseInt(answers.coats != null ? answers.coats : fall.default_coats, 10);
    var mw = !!answers.mit_werkzeug;
    var area = Number(answers.area_m2);

    var items = [];
    (variant.recipe || []).forEach(function (item) {
      // Werkzeug-abhängige Position (Maler-Set-Toggle) nur bei mit_werkzeug.
      if (item.werk && !mw) return;
      items.push(computeLine(item, area, coats, catalog));
    });

    // 3) Farb-Overlay: variant_id/Bild der farbführenden Position auf die Wahl setzen.
    if (answers.color) applyColor(fall, items, answers.color);

    var total = round2(items.reduce(function (s, li) { return s + round2(li.pack_count * li.unit_price); }, 0));

    return {
      status: pres.status, ehrlicher_pfad: false, produkt: pres.produkt,
      ueberschrift: pres.ueberschrift, nachricht: pres.nachricht,
      passt_fuer: pres.passt_fuer || [], passt_nicht_fuer: pres.passt_nicht_fuer || [],
      warnungen: pres.warnungen || [], events: pres.events || [],
      weiter_case_id: null,
      line_items: items, total: total,
    };
  }

  // Farbwahl: an der Position mit farbführendem catalog_key die variant_id (und, wenn
  // vorhanden, das Bild) auf die gewählte Farbe × aktuelle Gebinde-Größe setzen.
  function applyColor(fall, items, color) {
    var sw = fall.swatches || {};
    for (var i = 0; i < items.length; i++) {
      var li = items[i];
      var entry = sw[li.catalog_key];
      if (!entry) continue;
      var byColor = (entry.variants || {})[color];
      if (!byColor) continue;
      var size = sizeFromLabel(li.quantity_label);
      var sk = sizeKey(size);
      var vid = sk && byColor[sk];
      if (vid) {
        li.variant_id = vid;
        if (li.cart.length) li.cart[0].id = vid;
      }
      // Swatch-Bild der Farbe (falls vorhanden) als Thumbnail übernehmen.
      var swatch = (entry.swatches || []).filter(function (s) { return s.name === color; })[0];
      if (swatch && swatch.image) li.image = swatch.image;
    }
  }

  // --- Mengenrabatt (nur Anzeige; Shopify-Rabatt-App rechnet real im Warenkorb) ---
  function lineRate(count, tiers) {
    for (var i = 0; i < tiers.length; i++) if (count >= tiers[i].min) return tiers[i].rate;
    return 0;
  }
  function pricing(lineItems, tiers) {
    tiers = tiers || [{ min: 6, rate: 0.10 }, { min: 3, rate: 0.05 }];
    var lines = lineItems.map(function (li) {
      var base = round2(li.pack_count * li.unit_price);
      var rate = lineRate(li.pack_count, tiers);
      var total = round2(base * (1 - rate));
      return { base: base, total: total, saved: round2(base - total), rate: rate };
    });
    var subtotal = round2(lines.reduce(function (s, l) { return s + l.total; }, 0));
    var saved = round2(lines.reduce(function (s, l) { return s + l.saved; }, 0));
    return { lines: lines, subtotal: subtotal, saved: saved };
  }

  // --- Freitext-Einstieg (intent.py-Portierung, reines Keyword-Matching) ----------
  var STOP = {
    "ich": 1, "will": 1, "möchte": 1, "moechte": 1, "mein": 1, "meine": 1, "meinen": 1,
    "einen": 1, "eine": 1, "der": 1, "die": 1, "das": 1, "und": 1, "oder": 1, "mit": 1,
    "für": 1, "fuer": 1, "auf": 1, "in": 1, "im": 1, "wie": 1, "was": 1, "kann": 1,
    "man": 1, "soll": 1, "brauche": 1, "hilfe": 1, "bitte": 1, "neu": 1, "alte": 1,
    "streichen": 1, "lackieren": 1, "beschichten": 1, "lasieren": 1, "ölen": 1, "oelen": 1,
    "anstrich": 1, "farbe": 1, "streiche": 1
  };
  function tokens(text) {
    return (text || "").toLowerCase().split(/[^a-zäöüß0-9]+/)
      .filter(function (t) { return t.length > 2 && !STOP[t]; });
  }
  function searchCases(faelle, text, limit) {
    limit = limit || 3;
    var low = (text || "").toLowerCase();
    var toks = {}; tokens(text).forEach(function (t) { toks[t] = 1; });
    var treffer = [];
    faelle.forEach(function (f) {
      var score = 0, matched = [];
      (f.suchbegriffe || []).forEach(function (begriff) {
        var b = begriff.toLowerCase();
        if (low.indexOf(b) !== -1) { score += 2; matched.push(begriff); }
        else {
          var bt = tokens(begriff);
          for (var i = 0; i < bt.length; i++) { if (toks[bt[i]]) { score += 1; matched.push(begriff); break; } }
        }
      });
      if (score > 0) treffer.push({ case_id: f.case_id, titel: f.titel, score: score, matched: matched.slice(0, 4) });
    });
    treffer.sort(function (a, b) { return b.score - a.score; });
    return treffer.slice(0, limit);
  }

  // --- Engine-Fabrik: kapselt die geladene Datei + Lookups je case_id -------------
  function createEngine(data) {
    var byId = {};
    (data.faelle || []).forEach(function (f) { byId[f.case_id] = f; });
    return {
      data: data,
      faelle: (data.faelle || []).map(function (f) {
        return { case_id: f.case_id, titel: f.titel, intro: f.intro };
      }),
      getFall: function (id) { return byId[id] || null; },
      schema: function (id) {
        var f = byId[id]; return f ? f.schema : null;
      },
      swatchesFor: function (id, catalogKey) {
        var f = byId[id];
        var e = f && f.swatches && f.swatches[catalogKey];
        return e ? (e.swatches || []) : [];
      },
      evaluate: function (id, answers) {
        var f = byId[id];
        if (!f) throw new Error("Unbekannter Fall: " + id);
        return evaluate(f, answers, data.catalog || {});
      },
      pricing: function (lineItems) { return pricing(lineItems, data.volume_tiers); },
      search: function (text, limit) {
        return searchCases(data.faelle || [], text, limit);
      },
      volumeTiers: data.volume_tiers || [{ min: 6, rate: 0.10 }, { min: 3, rate: 0.05 }],
    };
  }

  return {
    createEngine: createEngine,
    evaluate: evaluate,
    pricing: pricing,
    searchCases: searchCases,
    sizeFromLabel: sizeFromLabel,
    optimiseSingleSize: optimiseSingleSize,
    _internals: { firstGate: firstGate, effectiveSelect: effectiveSelect, variantKey: variantKey },
  };
});
// Reines klassisches Skript (kein ESM-`export`) → im Browser via <script src> ladbar
// (setzt window.PlidAdvisorCore). Der Node-Golden-Test lädt diese Datei über einen
// kleinen Loader (scripts/advisor_golden_check.mjs), der globalThis.__PlidAdvisorCore liest.

